<template>
  <v-card class="mt-5" outlined>
    <v-card-title class="d-flex align-center">
      <v-icon class="mr-2" color="orange">mdi-alert-circle</v-icon>
      <span>Inventory Alerts</span>
      <v-badge
        v-if="alertCount > 0"
        :content="alertCount"
        color="red"
        class="ml-2"
        inline
      />
      <v-spacer />
      <v-btn icon @click="refreshAll" :loading="loading">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </v-card-title>
    <v-card-text>
      <!-- Summary Cards -->
      <v-row v-if="stock_summary" class="mb-4">
        <v-col cols="3">
          <v-card color="red" dark class="pa-3 text-center" flat>
            <div class="text-h4 font-weight-bold">
              {{ stock_summary.out_of_stock_count }}
            </div>
            <div class="caption">Out of Stock</div>
          </v-card>
        </v-col>
        <v-col cols="3">
          <v-card color="orange" dark class="pa-3 text-center" flat>
            <div class="text-h4 font-weight-bold">
              {{ stock_summary.low_stock_count }}
            </div>
            <div class="caption">Low Stock</div>
          </v-card>
        </v-col>
        <v-col cols="3">
          <v-card color="green" dark class="pa-3 text-center" flat>
            <div class="text-h4 font-weight-bold">
              {{ stock_summary.healthy_stock_count }}
            </div>
            <div class="caption">Healthy Stock</div>
          </v-card>
        </v-col>
        <v-col cols="3">
          <v-card color="blue" dark class="pa-3 text-center" flat>
            <div class="text-h4 font-weight-bold">
              {{ formatCurrency(stock_summary.total_inventory_value) }}
            </div>
            <div class="caption">Inventory Value (Cost)</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Tabs -->
      <v-tabs v-model="activeTab" class="mb-3">
        <v-tab>
          Out of Stock
          <v-badge
            v-if="out_of_stock_items.length"
            :content="out_of_stock_items.length"
            color="red"
            class="ml-2"
            inline
          />
        </v-tab>
        <v-tab>
          Low Stock
          <v-badge
            v-if="low_stock_items.length"
            :content="low_stock_items.length"
            color="orange"
            class="ml-2"
            inline
          />
        </v-tab>
        <v-tab>
          Healthy Stock
          <v-badge
            v-if="healthy_stock_items.length"
            :content="healthy_stock_items.length"
            color="green"
            class="ml-2"
            inline
          />
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <!-- Out of Stock Tab -->
        <v-tab-item>
          <v-text-field
            v-model="searchOutOfStock"
            prepend-icon="mdi-magnify"
            label="Search"
            dense
            class="mb-2"
          />
          <v-data-table
            :items="out_of_stock_items"
            :headers="headers"
            :search="searchOutOfStock"
            dense
          >
            <template v-slot:item.quantity="{ item }">
              <v-chip color="red" dark small>{{ item.quantity }}</v-chip>
            </template>
            <template v-slot:item.action="{ item }">
              <v-btn x-small color="primary" @click="openReorderDialog(item)"
                >Set Reorder Point</v-btn
              >
            </template>
          </v-data-table>
        </v-tab-item>

        <!-- Low Stock Tab -->
        <v-tab-item>
          <v-text-field
            v-model="searchLowStock"
            prepend-icon="mdi-magnify"
            label="Search"
            dense
            class="mb-2"
          />
          <v-data-table
            :items="low_stock_items"
            :headers="headersWithReorder"
            :search="searchLowStock"
            dense
          >
            <template v-slot:item.quantity="{ item }">
              <v-chip color="orange" dark small>{{ item.quantity }}</v-chip>
            </template>
            <template v-slot:item.action="{ item }">
              <v-btn x-small color="primary" @click="openReorderDialog(item)"
                >Edit Reorder Point</v-btn
              >
            </template>
          </v-data-table>
        </v-tab-item>

        <!-- Healthy Stock Tab -->
        <v-tab-item>
          <v-text-field
            v-model="searchHealthyStock"
            prepend-icon="mdi-magnify"
            label="Search"
            dense
            class="mb-2"
          />
          <v-data-table
            :items="healthy_stock_items"
            :headers="healthyStockHeaders"
            :search="searchHealthyStock"
            dense
          >
            <template v-slot:item.quantity="{ item }">
              <v-chip color="green" dark small>{{ item.quantity }}</v-chip>
            </template>
            <template v-slot:item.action="{ item }">
              <v-btn x-small color="primary" @click="openReorderDialog(item)"
                >Edit Reorder Point</v-btn
              >
            </template>
          </v-data-table>
        </v-tab-item>
      </v-tabs-items>

      <!-- Reorder Point Dialog -->
      <v-dialog v-model="reorderDialog" max-width="400">
        <v-card>
          <v-card-title>Set Reorder Point</v-card-title>
          <v-card-subtitle v-if="selectedItem">{{
            selectedItem.item_name
          }}</v-card-subtitle>
          <v-card-text>
            <v-text-field
              v-model.number="reorderForm.reorder_point"
              label="Reorder Point"
              type="number"
              min="0"
              outlined
              dense
            />
            <v-text-field
              v-model.number="reorderForm.min_stock_level"
              label="Min Stock Level"
              type="number"
              min="0"
              outlined
              dense
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="reorderDialog = false">Cancel</v-btn>
            <v-btn color="primary" @click="saveReorderPoint" :loading="saving"
              >Save</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
export default {
  data() {
    return {
      loading: false,
      saving: false,
      activeTab: 0,
      searchOutOfStock: "",
      searchLowStock: "",
      searchHealthyStock: "",
      reorderDialog: false,
      selectedItem: null,
      reorderForm: { reorder_point: 0, min_stock_level: 0 },
      headers: [
        { text: "Item Name", value: "item_name" },
        { text: "Item Type", value: "item_type" },
        { text: "Supplier", value: "supplier" },
        { text: "Qty", value: "quantity" },
        { text: "Unit Price", value: "unit_price" },
        { text: "Actions", value: "action", sortable: false },
      ],
      headersWithReorder: [
        { text: "Item Name", value: "item_name" },
        { text: "Item Type", value: "item_type" },
        { text: "Supplier", value: "supplier" },
        { text: "Qty", value: "quantity" },
        { text: "Reorder Point", value: "reorder_point" },
        { text: "Unit Price", value: "unit_price" },
        { text: "Actions", value: "action", sortable: false },
      ],
      healthyStockHeaders: [
        { text: "Item Name", value: "item_name" },
        { text: "Item Type", value: "item_type" },
        { text: "Supplier", value: "supplier" },
        { text: "Qty", value: "quantity" },
        { text: "Reorder Point", value: "reorder_point" },
        { text: "Unit Price", value: "unit_price" },
        { text: "Actions", value: "action", sortable: false },
      ],
    };
  },
  computed: {
    ...mapGetters({
      low_stock_items: "stock_alert/low_stock_items",
      out_of_stock_items: "stock_alert/out_of_stock_items",
      healthy_stock_items: "stock_alert/healthy_stock_items",
      stock_summary: "stock_alert/stock_summary",
    }),
    alertCount() {
      if (!this.stock_summary) return 0;
      return (
        this.stock_summary.out_of_stock_count +
        this.stock_summary.low_stock_count
      );
    },
  },
  methods: {
    ...mapActions({
      get_low_stock_items: "stock_alert/get_low_stock_items",
      get_out_of_stock_items: "stock_alert/get_out_of_stock_items",
      get_healthy_stock_items: "stock_alert/get_healthy_stock_items",
      get_stock_summary: "stock_alert/get_stock_summary",
      update_reorder_point: "stock_alert/update_reorder_point",
    }),
    formatCurrency(value) {
      if (!value) return "0.00";
      return Number(value).toLocaleString("en-PH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    async refreshAll() {
      this.loading = true;
      await Promise.all([
        this.get_stock_summary(),
        this.get_low_stock_items(),
        this.get_out_of_stock_items(),
        this.get_healthy_stock_items(),
      ]);
      this.loading = false;
    },
    openReorderDialog(item) {
      this.selectedItem = item;
      this.reorderForm.reorder_point = item.reorder_point || 0;
      this.reorderForm.min_stock_level = item.min_stock_level || 0;
      this.reorderDialog = true;
    },
    async saveReorderPoint() {
      this.saving = true;
      try {
        await this.update_reorder_point({
          item_id: this.selectedItem.id,
          reorder_point: this.reorderForm.reorder_point,
          min_stock_level: this.reorderForm.min_stock_level,
        });
        this.reorderDialog = false;
        await this.refreshAll();
      } catch (e) {
        this.$alert({
          title: "Error",
          message:
            "Error saving reorder point: " +
            (e.response?.data?.error || e.message),
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
      this.saving = false;
    },
  },
  async created() {
    await this.refreshAll();
  },
};
</script>
